import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, CalendarCheck, BookOpen, GraduationCap,
  Megaphone, Briefcase, UserCircle, LogOut, BellRing, ClipboardList,
  BarChart3, Award, ShieldCheck
} from "lucide-react";

const adminNav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/students", label: "Students", icon: Users },
  { to: "/batches", label: "Batches", icon: GraduationCap },
  { to: "/attendance", label: "Attendance", icon: CalendarCheck },
  { to: "/leaves", label: "Leave Requests", icon: ClipboardList },
  { to: "/materials", label: "Learning Materials", icon: BookOpen },
  { to: "/announcements", label: "Announcements", icon: Megaphone },
  { to: "/placement", label: "Placement", icon: Briefcase },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/user-management", label: "User Management", icon: ShieldCheck },
];

const studentNav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/attendance", label: "Attendance", icon: CalendarCheck },
  { to: "/leaves", label: "Leave Requests", icon: ClipboardList },
  { to: "/materials", label: "Learning Materials", icon: BookOpen },
  { to: "/announcements", label: "Announcements", icon: Megaphone },
  { to: "/placement", label: "Placement", icon: Briefcase },
  { to: "/certificates", label: "Certificates", icon: Award },
  { to: "/profile", label: "Profile", icon: UserCircle },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = user?.role === "student" ? studentNav : adminNav;

  // Team leads see admin nav minus User Management + Reports (Manager+ only)
  const filteredNav = user?.role === "team_lead"
    ? adminNav.filter((n) => !["/user-management", "/reports", "/students", "/batches"].includes(n.to))
    : nav;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (!user) return null;

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside className="w-64 fixed left-0 top-0 h-screen border-r border-[#E2E8F0] bg-white flex flex-col">
        <div className="px-6 py-5 border-b border-[#E2E8F0]">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_excel-learn-hub-2/artifacts/i2tti92b_image.png" alt="Quiddity" className="w-12 h-12 object-contain" />
            <div>
              <div className="font-heading font-bold text-[#0F172A] text-base leading-none">Quiddity</div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-[#64748B] mt-1">Institute of Excellence</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          {filteredNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors ${
                    isActive
                      ? "nav-item-active text-[#0F2E4A] font-medium"
                      : "text-[#475569] hover:text-[#0F2E4A] hover:bg-[#F8FAFC]"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.75} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-[#E2E8F0] p-4">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-[#059669] text-white grid place-items-center font-medium text-sm">
              {user.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#0F172A] truncate" data-testid="sidebar-user-name">{user.name}</div>
              <div className="text-[11px] uppercase tracking-wider text-[#64748B]">{user.role}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-[#475569] hover:text-[#DC2626] hover:bg-[#FEF2F2] rounded-md transition-colors"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.75} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="ml-64 flex-1 min-h-screen">
        <header className="h-16 border-b border-[#E2E8F0] bg-white/80 backdrop-blur-xl sticky top-0 z-40 px-8 flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.2em] text-[#64748B]">
            {user.role === "student" ? "Student Portal"
              : user.role === "manager" ? "Manager Portal"
              : user.role === "team_lead" ? "Team Lead Portal"
              : "Administrator Portal"}
          </div>
          <div className="flex items-center gap-3">
            <BellRing className="w-4 h-4 text-[#475569]" strokeWidth={1.75} />
            <div className="text-sm text-[#0F172A]">Welcome, <span className="font-medium">{user.name?.split(" ")[0]}</span></div>
          </div>
        </header>
        <main className="p-8 max-w-[1400px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
